import React, { useEffect, useState } from 'react';
import { AlertTriangle, ShieldAlert } from 'lucide-react';

interface AdComponentProps {
  adSlot?: string;
  format?: 'auto' | 'fluid' | 'rectangle' | 'horizontal' | 'vertical' | 'custom' | 'video' | 'text';
  layout?: 'in-article' | 'in-feed';
  className?: string;
  responsive: boolean;
  platform?: 'adsense' | 'medianet';
  width?: string;
  height?: string;
  defaultSlot?: boolean;
  videoConfig?: {
    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    maxHeight?: number;
  };
}

const AdComponent: React.FC<AdComponentProps> = ({
  adSlot,
  format = 'auto',
  layout,
  className = '',
  responsive = false,
  platform = 'adsense',
  defaultSlot = false,
  width,
  height,
}) => {
  const [isPWA] = useState(() => window.matchMedia('(display-mode: standalone)').matches);
  const [isVisible, setIsVisible] = useState(false);
  const [adError, setAdError] = useState(false);
  const [isAdBlockerDetected, setIsAdBlockerDetected] = useState(false);
  const [isAdLoaded, setIsAdLoaded] = useState(false);

  useEffect(() => {
    const checkAdBlocker = async () => {
      try {
        // More reliable ad blocker detection
        const testAd = document.createElement('div');
        testAd.innerHTML = '&nbsp;';
        testAd.className = 'adsbox';
        document.body.appendChild(testAd);
        
        const isBlocked = testAd.offsetHeight === 0;
        document.body.removeChild(testAd);
        
        setIsAdBlockerDetected(isBlocked);
      } catch (error) {
        setIsAdBlockerDetected(true);
      }
    };

    checkAdBlocker();
    
    // Initialize ads immediately
    setIsVisible(true);
    
    // Add styles for ad blocker detection
    const style = document.createElement('style');
    style.innerHTML = '.adsbox { height: 1px; width: 1px; position: absolute; left: -10000px; }';
    document.head.appendChild(style);
    
    const timer = setTimeout(() => {}, 0);
    
    return () => {
      document.head.removeChild(style);
      clearTimeout(timer);
    };
  }, [platform]);

  useEffect(() => {
    const loadAndInitializeAd = async () => {
      try {
        // Load AdSense script if not already loaded
        if (!document.querySelector('script[src*="adsbygoogle"]')) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-3638958253501972';
            script.async = true;
            script.crossOrigin = 'anonymous';
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        // Initialize the ad
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          setIsAdLoaded(true);
        } catch (error) {
          console.error('Error initializing ad:', error);
          setAdError(true);
        }

        // Set up mutation observer for ad container
        const adContainer = document.querySelector(platform === 'adsense' ? '.adsbygoogle' : '.media-net');
        if (adContainer) {
          const observer = new MutationObserver((mutations) => {
            const hasContent = mutations.some(mutation => 
              mutation.type === 'childList' && mutation.addedNodes.length > 0
            );
            if (hasContent) {
              setIsAdLoaded(true);
              observer.disconnect();
            }
          });
          observer.observe(adContainer, { childList: true, subtree: true });
        }
      } catch (error) {
        console.error('Error loading ad:', error);
        setAdError(true);
      }
    };

    if (isVisible && !isAdBlockerDetected) {
      loadAndInitializeAd();
    }

    return () => {
      // Clean up any observers or event listeners if component unmounts
      const adContainer = document.querySelector(platform === 'adsense' ? '.adsbygoogle' : '.media-net');
      if (adContainer) {
        const observer = new MutationObserver(() => {});
        observer.disconnect();
      }
    };
  }, [isVisible, isAdBlockerDetected, platform, adSlot, format]);

  if (!isVisible) return null;

  if (isAdBlockerDetected) {
    return (
      <div className="ad-container bg-yellow-50 p-4 rounded-lg shadow-md border border-yellow-200">
        <div className="flex items-center gap-3">
          <ShieldAlert className="h-5 w-5 text-yellow-600 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-yellow-800 mb-1">Ad Blocker Detected</h3>
            <p className="text-sm text-yellow-700">
              Angel is a free service supported by minimal, non-intrusive ads. Please disable your ad blocker to continue using our service.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (adError) {
    return (
      <div className={`ad-container bg-gray-50 p-4 rounded-lg ${className}`}>
        <p className="text-sm text-gray-500 text-center">Advertisement</p>
      </div>
    );
  }

  return (
    <div className={`ad-container ${className}`}>
      {platform === 'adsense' ? (
        <div className="relative">
          <ins
            className="adsbygoogle"
            style={{
              display: 'block',
              textAlign: 'center',
              minHeight: format === 'text' ? 'auto' : '100px',
              width: '100%',
              overflow: 'hidden',
              background: 'transparent'
            }}
            data-ad-client="ca-pub-3638958253501972"
            data-ad-slot={format === 'text' ? "4652088692" : "9433642084"}
            data-ad-format={format === 'text' ? "fluid" : "auto"}
            data-ad-layout-key={format === 'text' ? "-hf-7+2h-1m-4u" : undefined}
            data-full-width-responsive="true"
          />
          {!isAdLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 bg-opacity-50">
              <div className="text-xs text-gray-400">Loading advertisement...</div>
            </div>
          )}
        </div>
      ) : (
        <div
          id={adSlot}
          className="media-net"
          style={{
            minHeight: format === 'rectangle' ? '250px' : '60px',
            background: 'transparent',
          }}
        />
      )}
    </div>
  );
};

export default AdComponent;